import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useAllLearners } from '../services/learners';
import type { Learner } from '../types/learner';

/**
 * The audience list for course/program assignment pickers: every learner-role
 * member PLUS the signed-in member themself, injected at the top as "(You)"
 * when not already present.
 *
 * Consuming learning is open to every member (unified identity) — the
 * learner-role scoping of `useAllLearners` is a management-surface convention,
 * and the assignment API accepts any active membership. Surfacing "me" here
 * lets an admin enrol themself and experience a course exactly as their
 * learners will, without needing a second account.
 */
export function useAssignableLearners() {
  const { user } = useAuth();
  const query = useAllLearners();

  const data = useMemo<Learner[]>(() => {
    const base = query.data ?? [];
    if (!user || base.some((l) => l.id === user.id)) return base;
    // AuthUser is a slim identity shape; the Learner-only fields below aren't
    // rendered by the pickers, so neutral defaults are fine.
    const self: Learner = {
      id: user.id,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      full_name: `${user.full_name || `${user.first_name} ${user.last_name}`.trim()} (You)`,
      avatar_url: user.avatar_url ?? null,
      is_active: true,
      auth_provider: 'email',
      created_at: '',
      groups_count: 0,
    };
    return [self, ...base];
  }, [query.data, user]);

  return { ...query, data };
}
